import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import VideoRenewalPage from './pages/VideoRenewal/VideoRenewalPage';
import ExitPage from './pages/VideoRenewal/ExitPage';
import WhiteboardPage from './pages/Whiteboard/WhiteboardPage';
import { useThirdPartyHeader } from './hooks/useThirdPartyHeader';
import I18nDomTranslator from './i18n/I18nDomTranslator';

/**
 * Fallback: 쿼리 파라미터 유지하면서 /video로 리다이렉트.
 *
 * Jibri(recorder)는 stock jitsi-meet 방식으로 `https://<domain>/<room-uuid>#config.iAmRecorder=true`
 * 형태의 bare 경로를 연다(nginx가 이 단일 세그먼트 경로를 내부적으로 index.html로 리라이트).
 * 이 경로의 첫 세그먼트를 room id로 해석해 `/video?room=<id>`로 매핑하고,
 * recorder 감지에 쓰이는 `location.hash`도 함께 보존한다(Navigate는 넘기지 않으면 hash를 버림).
 *
 * 순수 루트("/")는 예외: 실제 Jibri는 이 경로를 먼저 방문해 Selenium
 * executeScript()로 localStorage를 주입한 뒤에야 방+해시 URL로 넘어간다. 이 첫 방문에서
 * <Navigate>로 클라이언트 사이드 재이동을 하면 그 스크립트 실행과 경합하면서
 * ChromeDriver가 "session deleted because of page crash"를 내는 것이 확인되어(실제
 * Jibri 로그로 검증), 방 세그먼트가 없을 때는 재이동 없이 같은 화면을 제자리에서 렌더한다.
 */
const FallbackRedirect: React.FC = () => {
  const location = useLocation();

  const roomId = location.pathname.split('/').filter(Boolean)[0] ?? '';

  if (!roomId) {
    return <VideoRenewalPage />;
  }

  const params = new URLSearchParams(location.search);
  if (!params.has('room')) {
    params.set('room', roomId);
  }
  const search = params.toString();

  return (
    <Navigate
      to={{
        pathname: '/video',
        search: search ? `?${search}` : '',
        hash: location.hash,
      }}
      replace
    />
  );
};

/**
 * 앱 루트 라우터.
 * - /video, /video2, /video3: 기존 Fabric.js 기반 VideoPage
 * - /video-renewal: Excalidraw 화이트보드 기반 VideoRenewalPage (v1 video-renewal 이식)
 * - /whiteboard: 독립 화이트보드 페이지 (모바일 웹뷰용)
 */
const App: React.FC = () => {
  useThirdPartyHeader();

  return (
    <>
      <I18nDomTranslator />
      <Routes>
        <Route path="/video" element={<VideoRenewalPage />} />
        <Route path="/video-renewal" element={<VideoRenewalPage />} />
        <Route path="/exit" element={<ExitPage />} />
        <Route path="/whiteboard" element={<WhiteboardPage />} />
        {/* 기타 경로는 /video로 (쿼리 파라미터 유지) */}
        <Route path="*" element={<FallbackRedirect />} />
      </Routes>
    </>
  );
};

export default App;
